import * as RSelect from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { forwardRef, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { focusRingInset } from '../lib/focus'
import { useFieldControlProps } from './Field'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  name?: string
  className?: string
  /** Required when the Select is not wrapped in a <Field>. */
  'aria-label'?: string
}

export function Select({
  value,
  defaultValue,
  onValueChange,
  options,
  placeholder = 'Select…',
  disabled,
  name,
  className,
  ...rest
}: SelectProps) {
  const fieldProps = useFieldControlProps()
  return (
    <RSelect.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
      name={name}
    >
      <RSelect.Trigger
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border-control',
          'bg-surface-inset px-3 text-body-sm text-text-primary',
          'hover:border-border-strong disabled:cursor-not-allowed disabled:opacity-50',
          'aria-[invalid=true]:border-state-critical data-[placeholder]:text-text-tertiary',
          focusRingInset,
          className,
        )}
        {...fieldProps}
        {...rest}
      >
        <RSelect.Value placeholder={placeholder} />
        <RSelect.Icon>
          <ChevronDown aria-hidden className="size-4 text-text-secondary" />
        </RSelect.Icon>
      </RSelect.Trigger>

      <RSelect.Portal>
        <RSelect.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'z-modal max-h-72 min-w-[--radix-select-trigger-width] overflow-hidden rounded-md',
            'border border-border-subtle bg-surface-overlay shadow-lg',
            'data-[state=open]:animate-fade-in',
          )}
        >
          <RSelect.Viewport className="p-1">
            {options.map((option) => (
              <RSelect.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative flex cursor-pointer select-none items-center gap-2 rounded-sm py-2 pe-2 ps-8',
                  'text-body-sm text-text-primary outline-none',
                  'data-[highlighted]:bg-surface-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                <RSelect.ItemIndicator className="absolute start-2 flex items-center">
                  <Check aria-hidden className="size-4 text-brand-primary" />
                </RSelect.ItemIndicator>
                <RSelect.ItemText>{option.label}</RSelect.ItemText>
              </RSelect.Item>
            ))}
          </RSelect.Viewport>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  )
}

/** Native select for dense table filters, where a portal popover is overkill. */
export const NativeSelect = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }
>(function NativeSelect({ className, children, ...rest }, ref) {
  const fieldProps = useFieldControlProps()
  return (
    <select
      ref={ref}
      className={cn(
        'h-10 w-full rounded-md border border-border-control bg-surface-inset px-3 text-body-sm text-text-primary',
        focusRingInset,
        className,
      )}
      {...fieldProps}
      {...rest}
    >
      {children}
    </select>
  )
})
